import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSupabase } from '../providers/SupabaseProvider';
import { useAuth } from '../providers/SupabaseProvider';
import { Loader2 } from 'lucide-react';

export default function SubscriptionGuard({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const supabase = useSupabase();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 3;
  const [organizationId, setOrganizationId] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    async function checkSubscription() {
      try {
        // If no user, don't check subscription
        if (!user?.id) { 
          if (isMounted) {
            setLoading(false);
            setHasActiveSubscription(false);
            setIsNewUser(false);
          }
          return;
        }

        // If user needs to setup password, don't check subscription
        if (user.user_metadata?.redirectTo === 'setup-password') { 
          if (isMounted) {
            setLoading(false);
            setHasActiveSubscription(false);
            setIsNewUser(false);
          }
          return;
        }
        
        try {
          // 1. Get organization_id from organization_users
          const { data: orgEntries, error: orgUserError } = await supabase
            .from('organization_users')
            .select('organization_id')
            .eq('user_id', user.id)
            .maybeSingle();
          
          if (orgUserError) throw new Error(`Fehler beim Laden der Organisation: ${orgUserError.message}`);
          
          if (!orgEntries || !orgEntries.organization_id) {
            // No organization found or organization ID is null, user needs to create one
            if (isMounted) {
              setIsNewUser(true);
              setHasActiveSubscription(false);
              setOrganizationId(null);
              setLoading(false);
            }
            return; 
          }

          // Store organization ID for later use
          if (isMounted) {
            setOrganizationId(orgEntries.organization_id);
          }
          
          // Function to check subscription for a given organization ID 
          const checkSubscriptionForOrg = async (orgId: string) => {
            try {
              // 2. Get subscription_id from organizations
              const { data: organization, error: orgError } = await supabase
                .from('organizations')
                .select('subscription_id')
                .eq('id', orgId)
                .maybeSingle(); 

              // If no subscription_id exists yet, return false
              if (orgError || !organization?.subscription_id) {
                if (isMounted) setHasActiveSubscription(false);
                return;
              }

              // 3. Check status from paypal_subscriptions
              const { data: subscription, error: subError } = await supabase
                .from('paypal_subscriptions')
                .select('status')
                .eq('id', organization.subscription_id)
                .maybeSingle(); 

              // If no subscription exists yet, return false
              if (subError || !subscription) {
                if (isMounted) setHasActiveSubscription(false);
                return;
              }

              if (isMounted) {
                setHasActiveSubscription(subscription?.status === 'active'); 
              }
            } catch (err) {
              console.error('Error checking subscription for org:', err);
              if (isMounted) setHasActiveSubscription(false);
            }
          };

          // Check subscription for the organization
          await checkSubscriptionForOrg(orgEntries.organization_id);
           
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'Unbekannter Fehler';
          console.error('Error checking subscription:', errorMessage);
          
          if (isMounted) {
            setHasActiveSubscription(false);
            setConnectionError(errorMessage);
            
            // Retry logic with exponential backoff
            if (retryCount < MAX_RETRIES) {
              const delay = 1000 * Math.pow(2, retryCount);
              console.log(`Retrying in ${delay}ms (attempt ${retryCount + 1}/${MAX_RETRIES})`);
              
              setTimeout(() => {
                if (isMounted) {
                  setRetryCount(prev => prev + 1);
                  checkSubscription();
                }
              }, delay);
            }
          }
        }
      } catch (err) {
        console.error('Error in subscription check:', err); 
        if (isMounted) setHasActiveSubscription(false); 
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    checkSubscription();
    
    return () => { isMounted = false; };
  }, [user, supabase]);

  const handleRetry = () => {
    setRetryCount(0);
    setConnectionError(null);
    setLoading(true);
    checkSubscription();
  };

  // Show error state with retry button
  if (connectionError && !loading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl max-w-md w-full">
          <div className="text-center mb-6">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Verbindungsfehler</h2>
          </div>
          <p className="text-sm text-red-600 dark:text-red-400 mb-6">{connectionError}</p>
          <button onClick={handleRetry} className="w-full py-2 px-4 bg-primary-600 hover:bg-primary-700 text-white rounded-md font-medium">
            Erneut versuchen
          </button>
        </div>
      </div>
    );
  }

  // Wait until loading is finished 
  if (loading) { 
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600 mb-4" />
        {retryCount > 0 && (
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Verbindungsversuch {retryCount + 1} von {MAX_RETRIES + 1}...
          </p>
        )}
      </div>
    );
  }

  // setup-password is allowed through, even without subscription
  if (location.pathname.includes('/setup-password')) {
    return <>{children}</>;
  }

  // If redirectTo === setup-password -> redirect
  if (user?.user_metadata?.redirectTo === 'setup-password') {
    return <Navigate to="/setup-password" replace />;
  }

  // If new user, show organization creation page
  if (isNewUser) {
    // Allow the user to proceed to the dashboard to create an organization
    if (location.pathname === '/dashboard') {
      return <>{children}</>;
    }
    return <Navigate to="/dashboard" replace />;
  }

  // If no subscription exists -> redirect to subscription page
  if (!hasActiveSubscription) {
    // Only redirect to subscription if user has an organization
    if (organizationId) {
      return <Navigate to="/dashboard/subscription" replace />;
    } else {
      // If no organization, redirect to dashboard to create one
      return <Navigate to="/dashboard" replace />;
    }
  }

  // ✅ All good
  return <>{children}</>;
}